import React, { useState, useEffect } from 'react';
import { useGroup } from '../../contexts/GroupContext';
import { Users, Check, X, Loader } from 'lucide-react';

/**
 * JoinGroup page - handles /join/:inviteCode routes
 * 
 * Usage in your router:
 * <Route path="/join/:inviteCode" element={<JoinGroup />} />
 */
const JoinGroup = ({ inviteCode, user, onSuccess, onCancel }) => {
  const { joinGroup, loading } = useGroup();
  const [status, setStatus] = useState('ready'); // ready, joining, success, error
  const [error, setError] = useState('');
  const [joinedGroup, setJoinedGroup] = useState(null);

  const handleJoin = async () => {
    if (!inviteCode) {
      setError('No invite code provided');
      setStatus('error');
      return;
    }

    setStatus('joining');
    setError('');

    try {
      const group = await joinGroup(inviteCode);
      setJoinedGroup(group);
      setStatus('success');
      
      // Redirect after success
      setTimeout(() => {
        onSuccess?.();
      }, 2000);
    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  };

  // If user isn't logged in, show message
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users size={32} className="text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            You've Been Invited!
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Sign in or create an account to join this band.
          </p>
          <div className="bg-gray-100 dark:bg-gray-700 rounded-xl p-4 mb-6">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Invite Code</p>
            <p className="text-xl font-bold text-gray-800 dark:text-gray-100 tracking-wider">
              {inviteCode}
            </p>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            After signing in, you'll automatically join the band.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
        {status === 'ready' && (
          <>
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users size={32} className="text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              Join a Band
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You've been invited to collaborate on Band Dad.
            </p>
            
            <div className="bg-gray-100 dark:bg-gray-700 rounded-xl p-4 mb-6">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Invite Code</p>
              <p className="text-xl font-bold text-gray-800 dark:text-gray-100 tracking-wider">
                {inviteCode}
              </p>
            </div>

            <button
              onClick={handleJoin}
              className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors mb-3"
            >
              Join Band
            </button>
            
            <button
              onClick={onCancel}
              className="w-full py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
            >
              Cancel
            </button>
          </>
        )}

        {status === 'joining' && (
          <>
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader size={32} className="text-blue-600 dark:text-blue-400 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              Joining...
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we add you to the band.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-green-600 dark:text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              Welcome!
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You've joined <span className="font-semibold">{joinedGroup?.name}</span>
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Redirecting to dashboard...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <X size={32} className="text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              Unable to Join
            </h1>
            <p className="text-red-600 dark:text-red-400 mb-6">
              {error}
            </p>
            
            <button
              onClick={() => setStatus('ready')}
              className="w-full py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-xl font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors mb-3"
            >
              Try Again
            </button>
            
            <button
              onClick={onCancel}
              className="w-full py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
            >
              Go to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Manual invite code entry component
 * Use this if you want users to manually enter codes
 */
export const JoinGroupManual = ({ user, onSuccess, onCancel }) => {
  const { joinGroup } = useGroup();
  const [inviteCode, setInviteCode] = useState('');
  const [status, setStatus] = useState('ready');
  const [error, setError] = useState('');
  const [joinedGroup, setJoinedGroup] = useState(null);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;

    setStatus('joining');
    setError('');

    try {
      const group = await joinGroup(inviteCode.trim().toUpperCase());
      setJoinedGroup(group);
      setStatus('success');
      setTimeout(() => onSuccess?.(), 2000);
    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md">
        {status === 'ready' || status === 'error' ? (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users size={32} className="text-blue-600 dark:text-blue-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                Join a Band
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Enter the invite code you received
              </p>
            </div>

            <form onSubmit={handleJoin}>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="ABCD1234"
                className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-blue-500 text-center text-2xl font-bold tracking-widest uppercase"
                maxLength={8}
                autoFocus
              />

              {error && (
                <p className="mt-3 text-red-600 dark:text-red-400 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={!inviteCode.trim()}
                className="w-full mt-4 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Join Band
              </button>
            </form>

            <button
              onClick={onCancel}
              className="w-full mt-3 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
            >
              Cancel
            </button>
          </>
        ) : status === 'joining' ? (
          <div className="text-center py-8">
            <Loader size={48} className="text-blue-600 dark:text-blue-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Joining band...</p>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Welcome!</h2>
            <p className="text-gray-600 dark:text-gray-400">
              You've joined <span className="font-semibold">{joinedGroup?.name}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinGroup;